import { Link } from "react-router-dom";
import { ArrowRight, Phone, ChevronRight, Check } from "lucide-react";
import SEO, { SITE } from "../components/site/SEO";
import BookingForm from "../components/site/BookingForm";
import Footer from "../components/site/Footer";
import { Reveal } from "../components/site/Reveal";
import { WhatsAppIcon } from "../components/site/Icons";
import { useSettings } from "../context/SettingsContext";
import { buildWhatsAppLink, telHref, BUSINESS_PHONE_DISPLAY } from "../lib/whatsapp";

function CTAStrip() {
  const wa = buildWhatsAppLink();
  return (
    <div className="mt-8 flex flex-wrap gap-3">
      <a href="#booking" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-navy-deep font-medium px-6 h-12 rounded-full">Get Tour Quote <ArrowRight className="w-4 h-4" /></a>
      <a href={telHref} data-testid="cta-call" className="inline-flex items-center gap-2 bg-white border border-border text-navy px-6 h-12 rounded-full"><Phone className="w-4 h-4" /> Call Now</a>
      <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 h-12 rounded-full"><WhatsAppIcon className="w-4 h-4" /> WhatsApp</a>
    </div>
  );
}

function PageShell({ title, description, path, kicker, heading, intro, image, jsonLd, children }) {
  return (
    <>
      <SEO title={title} description={description} path={path} image={image} jsonLd={jsonLd} />
      {/* Hero */}
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-24 bg-navy text-white overflow-hidden">
        <img src={image} alt={heading} className="absolute inset-0 w-full h-full object-cover opacity-30" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 to-navy-deep/85" />
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-8">
          <p className="text-gold text-xs uppercase tracking-[0.25em] mb-4">{kicker}</p>
          <h1 className="font-heading text-4xl sm:text-6xl leading-[1.05] tracking-tight max-w-4xl">{heading}</h1>
          <p className="mt-5 text-white/80 max-w-2xl text-base sm:text-lg leading-relaxed">{intro}</p>
        </div>
      </section>
      {/* Content */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-8 py-14 sm:py-20">
        {children}
      </section>
      {/* Booking */}
      <section id="booking" className="bg-bone py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Book</p>
            <h2 className="text-navy text-3xl sm:text-4xl">Plan Your Hyderabad to Srisailam Trip</h2>
          </div>
          <BookingForm />
        </div>
      </section>
      <Footer />
    </>
  );
}

function Bullet({ children }) { return <li className="flex items-start gap-2.5"><Check className="w-5 h-5 text-gold mt-0.5 shrink-0" /><span>{children}</span></li>; }

/* ---------------- Hyderabad to Srisailam ---------------- */
export function HyderabadToSrisailam() {
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Service",
    serviceType: "Hyderabad to Srisailam cab & tour service", provider: { "@type": "TravelAgency", name: "RSK Tours", url: SITE },
    areaServed: ["Hyderabad", "Srisailam"], name: "Hyderabad to Srisailam Cab & Tour Packages",
  };
  return (
    <PageShell
      title="Hyderabad to Srisailam Cab & Tour Packages | RSK Tours"
      description="Book Hyderabad to Srisailam cab services, one-day tour, round-trip and customized tour packages with RSK Tours. Comfortable travel, experienced drivers, flexible options."
      path="/hyderabad-to-srisailam" kicker="Route" heading="Hyderabad to Srisailam — Cab & Tour"
      intro="Reliable pickups from Hyderabad, an easy road journey through the Nallamala hills, guided darshan at Srisailam and a comfortable return — all handled by RSK Tours."
      image="https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/zjdxr0t0_images%20%2813%29.jpeg"
      jsonLd={jsonLd}
    >
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-14 items-start">
        <Reveal>
          <h2 className="text-navy text-3xl sm:text-4xl mb-4">A comfortable road journey, planned end-to-end</h2>
          <p className="text-muted2 leading-relaxed">Srisailam is about 213 km south of Hyderabad. Our cabs pick you up from a location of your choice, take the scenic Nallamala route and get you to the temple with time for a peaceful darshan.</p>
          <ul className="mt-6 space-y-2.5 text-navy/80">
            <Bullet>Hyderabad pickup from your doorstep or a convenient boarding point</Bullet>
            <Bullet>Sedan, SUV, Innova Crysta or Tempo Traveller — matched to your group size</Bullet>
            <Bullet>Punctual departure, safe driving and clean, AC vehicles</Bullet>
            <Bullet>One-way, round-trip, one-day tour or fully customized itinerary</Bullet>
            <Bullet>Guided help with darshan, prasadam and local sightseeing</Bullet>
          </ul>
          <CTAStrip />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="bg-white rounded-2xl border border-border p-6 shadow-card">
            <h3 className="text-navy text-2xl mb-4">Route at a glance</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-muted2">Distance</dt><dd className="text-navy font-medium">~213 km</dd></div>
              <div className="flex justify-between"><dt className="text-muted2">Drive time</dt><dd className="text-navy font-medium">4–5 hours</dd></div>
              <div className="flex justify-between"><dt className="text-muted2">Pickup</dt><dd className="text-navy font-medium">Anywhere in Hyderabad</dd></div>
              <div className="flex justify-between"><dt className="text-muted2">Return</dt><dd className="text-navy font-medium">Same day / next day</dd></div>
              <div className="flex justify-between"><dt className="text-muted2">Trip types</dt><dd className="text-navy font-medium">One-way · Round · One-day · Custom</dd></div>
            </dl>
            <Link to="/srisailam-tour-packages" className="mt-6 inline-flex items-center gap-1.5 text-navy hover:text-gold">See tour packages <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </Reveal>
      </div>
    </PageShell>
  );
}

/* ---------------- Tour packages ---------------- */
export function TourPackages() {
  const jsonLd = {
    "@context": "https://schema.org", "@type": "ItemList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Hyderabad to Srisailam One Day Tour", url: SITE + "/trips/srisailam-one-day-darshan-yatra" },
      { "@type": "ListItem", position: 2, name: "Hyderabad to Srisailam Round Trip (2 Days)", url: SITE + "/trips/srisailam-two-day-temple-nallamala-yatra" },
      { "@type": "ListItem", position: 3, name: "Srisailam Temple Tour" },
      { "@type": "ListItem", position: 4, name: "Srisailam Sightseeing Tour" },
      { "@type": "ListItem", position: 5, name: "Customized Srisailam Tour" },
    ],
  };
  const pkgs = [
    { title: "Hyderabad to Srisailam One Day Tour", desc: "Same-day darshan trip with early departure and late-night return.", slug: "srisailam-one-day-darshan-yatra" },
    { title: "Hyderabad to Srisailam Round Trip (2 Days)", desc: "Two-day itinerary — temple, Nallamala, Patalaganga, caves.", slug: "srisailam-two-day-temple-nallamala-yatra" },
    { title: "Srisailam Temple Tour", desc: "Focused visit to Mallikarjuna Swamy and Bhramaramba Devi." },
    { title: "Srisailam Sightseeing Tour", desc: "Dam, viewpoints, Patalaganga and Nallamala forest." },
    { title: "Customized Srisailam Tour", desc: "Design your own itinerary — pickup, vehicle and stops your way." },
  ];
  return (
    <PageShell
      title="Srisailam Tour Packages from Hyderabad | RSK Tours"
      description="Explore Srisailam tour packages from Hyderabad with RSK Tours — one-day tour, round trip, temple tour, sightseeing and customized packages. Get a quote today."
      path="/srisailam-tour-packages" kicker="Packages" heading="Srisailam Tour Packages from Hyderabad"
      intro="Pick a ready-made package or build your own — every option is bookable end-to-end with RSK Tours."
      image="https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/0mmubo69_images%20%286%29.jpeg"
      jsonLd={jsonLd}
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {pkgs.map((p, i) => (
          <Reveal key={p.title} delay={(i % 3) * 0.05}>
            <article className="bg-white border border-border rounded-2xl p-6 shadow-card h-full flex flex-col">
              <h3 className="text-navy text-xl leading-tight">{p.title}</h3>
              <p className="text-muted2 text-sm mt-2 flex-1">{p.desc}</p>
              <div className="mt-5 flex items-center justify-between">
                <span className="text-navy font-medium">Get Quote</span>
                {p.slug
                  ? <Link to={`/trips/${p.slug}`} className="text-navy hover:text-gold inline-flex items-center gap-1 text-sm">View Details <ArrowRight className="w-4 h-4" /></Link>
                  : <a href="#booking" className="text-navy hover:text-gold inline-flex items-center gap-1 text-sm">Enquire <ArrowRight className="w-4 h-4" /></a>}
              </div>
            </article>
          </Reveal>
        ))}
      </div>
      <CTAStrip />
    </PageShell>
  );
}

/* ---------------- Sightseeing ---------------- */
export function Sightseeing() {
  const places = [
    { name: "Mallikarjuna Swamy Temple", desc: "One of the twelve sacred Jyotirlingas of Shiva." },
    { name: "Bhramaramba Devi Temple", desc: "One of the eighteen Shakti Peethas." },
    { name: "Sakshi Ganapati Temple", desc: "A short drive from the main temple." },
    { name: "Pathala Ganga", desc: "Sacred Krishna river ghat below the temple." },
    { name: "Srisailam Dam", desc: "Massive dam across the Krishna river." },
    { name: "Octopus View Point", desc: "Panoramic view over the Nallamala hills." },
    { name: "Akka Mahadevi Caves", desc: "Meditation caves reached by boat (subject to water level)." },
  ];
  return (
    <PageShell
      title="Srisailam Sightseeing Tour | Places to Visit | RSK Tours"
      description="Discover the top places to visit in Srisailam with RSK Tours — Srisailam temple, dam, Pathala Ganga, Sakshi Ganapati, Octopus View Point and more."
      path="/srisailam-sightseeing" kicker="Sightseeing" heading="Srisailam Sightseeing Tour"
      intro="Beyond the temple, Srisailam offers stunning viewpoints, sacred river ghats, dams and caves. We plan a comfortable circuit around your temple visit."
      image="https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/14i70ux5_images%20%282%29.jpeg"
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {places.map((p, i) => (
          <Reveal key={p.name} delay={(i % 3) * 0.05}>
            <div className="bg-white border border-border rounded-2xl p-6 shadow-card">
              <h3 className="text-navy text-xl">{p.name}</h3>
              <p className="text-muted2 text-sm mt-2">{p.desc}</p>
              <a href="#booking" className="mt-4 inline-flex items-center gap-1.5 text-navy hover:text-gold text-sm">Include in my tour <ChevronRight className="w-4 h-4" /></a>
            </div>
          </Reveal>
        ))}
      </div>
      <CTAStrip />
    </PageShell>
  );
}

/* ---------------- Cab service ---------------- */
export function CabService() {
  return (
    <PageShell
      title="Hyderabad to Srisailam Cab Service | RSK Tours"
      description="Book a reliable Hyderabad to Srisailam cab with RSK Tours — sedan, SUV, Innova Crysta or Tempo Traveller. One-way, round-trip and one-day tour cabs."
      path="/srisailam-cab-service" kicker="Cab Service" heading="Hyderabad to Srisailam Cab Service"
      intro="Comfortable, experienced-driver cabs from Hyderabad to Srisailam — sized for couples, families and larger groups."
      image="https://images.unsplash.com/photo-1502877338535-766e1452684a?w=1200"
    >
      <div className="grid lg:grid-cols-2 gap-10">
        <Reveal>
          <h2 className="text-navy text-3xl sm:text-4xl mb-4">What&apos;s included</h2>
          <ul className="space-y-2.5 text-navy/80">
            <Bullet>Doorstep pickup within Hyderabad</Bullet>
            <Bullet>AC vehicle in your preferred category</Bullet>
            <Bullet>Experienced driver familiar with the Srisailam route</Bullet>
            <Bullet>Toll and parking assistance (billed as per actuals)</Bullet>
            <Bullet>One-way, round-trip or one-day tour option</Bullet>
          </ul>
          <CTAStrip />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="bg-white rounded-2xl border border-border p-6 shadow-card">
            <h3 className="text-navy text-2xl mb-3">Cab types available</h3>
            <ul className="text-navy/80 divide-y divide-border">
              <li className="py-3 flex justify-between"><span>Sedan (Dzire / Etios)</span><span className="text-muted2 text-sm">4 pax</span></li>
              <li className="py-3 flex justify-between"><span>SUV (Ertiga / Innova)</span><span className="text-muted2 text-sm">6 pax</span></li>
              <li className="py-3 flex justify-between"><span>Premium SUV (Innova Crysta)</span><span className="text-muted2 text-sm">6–7 pax</span></li>
              <li className="py-3 flex justify-between"><span>Tempo Traveller</span><span className="text-muted2 text-sm">12+ pax</span></li>
            </ul>
            <p className="mt-4 text-xs text-muted2">Final make & model confirmed at booking. Fares are quoted individually.</p>
          </div>
        </Reveal>
      </div>
    </PageShell>
  );
}

/* ---------------- Contact ---------------- */
export function Contact() {
  const { settings } = useSettings();
  const wa = buildWhatsAppLink();
  return (
    <PageShell
      title="Contact RSK Tours | Hyderabad to Srisailam"
      description="Contact RSK Tours for Hyderabad to Srisailam cab bookings and tour packages. Call, WhatsApp or email — we&apos;ll reply with a quote quickly."
      path="/contact" kicker="Contact" heading="Talk to RSK Tours"
      intro="We usually reply on WhatsApp within minutes. Send us your travel dates and preferred vehicle and we&apos;ll come back with a quote."
      image="https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/ovq5nlsj_images%20%288%29.jpeg"
    >
      <div className="grid lg:grid-cols-2 gap-10">
        <Reveal>
          <div className="space-y-4 text-navy">
            {settings?.contact_name && <p className="text-2xl">{settings.contact_name}</p>}
            <a href={telHref} className="flex items-center gap-3 hover:text-gold" data-testid="contact-phone"><Phone className="w-5 h-5 text-gold" />{BUSINESS_PHONE_DISPLAY}</a>
            <a href={wa} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-gold" data-testid="contact-whatsapp"><WhatsAppIcon className="w-5 h-5 text-[#25D366]" />WhatsApp us</a>
            {settings?.email && <a href={`mailto:${settings.email}`} className="flex items-center gap-3 hover:text-gold" data-testid="contact-email"><span className="w-5 h-5 text-gold">@</span>{settings.email}</a>}
            {settings?.address && <p className="flex items-start gap-3 text-navy/80"><span className="w-5 h-5 text-gold mt-0.5">◎</span>{settings.address}</p>}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <BookingForm compact />
        </Reveal>
      </div>
    </PageShell>
  );
}
